import './internal-clients.css';

import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import useCreateClientAdminUser from '../../api/super-admin/clients/useCreateClientAdminUser';
import useGetClient from '../../api/super-admin/clients/useGetClient';
import useSaveClient from '../../api/super-admin/clients/useSaveClient';
import useUploadClientLogo from '../../api/super-admin/clients/useUploadClientLogo';
import useGetRequest from '../../api/useGetRequest';
import MainButton from '../../components/agv-button';
import GenericModal from '../../components/agv-generic-modal';
import Stepper from '../../components/agv-stepper';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import AgvPageHeader from '../../redesign-v4/components/page-header';
import { EMPTY_CLIENT_INFORMATION } from './client-defaults';
import { toClientPayload, toClientsOptions } from './client-form';
import { Panel } from './clients-ui';
import ClientCreationSummary from './components/client-creation-summary';
import AdminUserStep from './steps/admin-user-step';
import InformationStep, {
  isInformationStepValid,
} from './steps/information-step';
import OptionsStep from './steps/options-step';
import PricingStep, { isPricingValid } from './steps/pricing-step';

const STEP = {
  INFORMATION: 0,
  OPTIONS: 1,
  PRICING: 2,
  ADMIN: 3,
  SUMMARY: 4,
};

const STEPS = [
  { label: 'Informations' },
  { label: 'Options' },
  { label: 'Tarification' },
  { label: 'Administrateurs' },
  { label: 'Récapitulatif' },
];

/**
 * Création d'un client : assistant linéaire, sans saut d'étape (le stepper
 * n'est pas cliquable, « Modifier » depuis le récapitulatif ramène à une
 * étape). Rien n'est enregistré avant « Créer le client » : le client, son
 * logo puis ses administrateurs sont créés à la fin, dans cet ordre.
 */
export default function ClientCreateWizard({ onCreated, onExit }) {
  const [step, setStep] = useState(STEP.INFORMATION);
  const [information, setInformation] = useState(EMPTY_CLIENT_INFORMATION);
  const [featuresV2, setFeaturesV2] = useState(null);
  // Options de départ (par défaut, ou copiées d'un client) : voir FeatureTree.
  const [baselineFeaturesV2, setBaselineFeaturesV2] = useState(null);
  const [pricing, setPricing] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
  // Verrou synchrone : un double-clic part avant que `isCreating` ne
  // désactive le bouton, il ne doit pas créer le client deux fois.
  const isCreatingRef = useRef(false);

  const { data: clientsData } = useGetRequest('/super-admin/clients', 0);
  const { data: defaults } = useGetRequest('/super-admin/clients/defaults', 0);
  const { isLoading: isFetching, fetchClient } = useGetClient();
  const { saveClient } = useSaveClient();
  const { uploadLogo } = useUploadClientLogo();
  const { createAdminUser } = useCreateClientAdminUser();

  // Valeurs par défaut de l'API, appliquées une seule fois : la saisie n'est
  // jamais écrasée ensuite.
  useEffect(() => {
    if (!defaults || featuresV2 != null) return;

    setFeaturesV2(defaults.features_v2 ?? {});
    setBaselineFeaturesV2(defaults.features_v2 ?? {});
    setPricing(defaults.pricing ?? {});
  }, [defaults, featuresV2]);

  const clientsOptions = useMemo(
    () => toClientsOptions(clientsData?.clients),
    [clientsData]
  );

  const isInformationValid = isInformationStepValid(information);
  const isPricingStepValid = isPricingValid(pricing);
  const isHydrated = featuresV2 != null;
  // Saisie à protéger en quittant : tout écart avec le formulaire vide et les
  // valeurs par défaut (options et tarification comprises).
  const hasInput =
    JSON.stringify(information) !== JSON.stringify(EMPTY_CLIENT_INFORMATION) ||
    (isHydrated &&
      (JSON.stringify(featuresV2) !== JSON.stringify(defaults?.features_v2) ||
        JSON.stringify(pricing) !== JSON.stringify(defaults?.pricing))) ||
    logoFile != null ||
    adminUsers.length > 0;

  const isNextDisabled =
    !isHydrated ||
    (step === STEP.INFORMATION && !isInformationValid) ||
    (step === STEP.PRICING && !isPricingStepValid);

  const handleCopyFromClient = async (sourceClientId) => {
    const sourceClient = await fetchClient(sourceClientId);
    if (!sourceClient) return;

    setFeaturesV2(sourceClient.features_v2 || {});
    setBaselineFeaturesV2(sourceClient.features_v2 || {});
    setPricing({
      ...(defaults?.pricing || {}),
      ...(sourceClient.pricing || {}),
    });
    toast.success(
      `Options et tarification copiées depuis « ${sourceClient.name} ».`
    );
  };

  const createClient = async () => {
    if (isCreatingRef.current) return;

    isCreatingRef.current = true;
    setIsCreating(true);

    try {
      const client = await saveClient(
        null,
        toClientPayload({ information, featuresV2, pricing })
      );
      if (!client) return;

      // Le client existe désormais : un logo ou un administrateur refusé
      // (email déjà utilisé…) n'arrête pas le parcours, sinon une nouvelle
      // tentative serait refusée (nom déjà utilisé). La fiche signale ce qui
      // reste à faire.
      const isLogoMissing =
        logoFile != null && !(await uploadLogo(client.client_id, logoFile));

      const failedAdminEmails = [];
      for (const adminUser of adminUsers) {
        const createdAdminUsers = await createAdminUser(
          client.client_id,
          adminUser
        );
        if (!createdAdminUsers) failedAdminEmails.push(adminUser.email);
      }

      onCreated(client.client_id, { isLogoMissing, failedAdminEmails });
    } finally {
      isCreatingRef.current = false;
      setIsCreating(false);
    }
  };

  const exit = () => {
    if (hasInput) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    onExit();
  };

  return (
    <div className="ic-page">
      <AgvPageHeader
        icon={faBuilding}
        breadcrumb={['Outils internes', 'Clients', 'Nouveau client']}
        title={information.name.trim() || 'Nouveau client'}
        summary={`Étape ${step + 1} sur ${STEPS.length} · ${STEPS[step].label}`}
      />

      <div className="ic-page__body">
        <div className="ic-page__content">
          <div className="ic-stepper">
            <Stepper steps={STEPS} currentStep={step} />
          </div>

          {!isHydrated ? (
            <Panel>
              <p className="tw-m-0 tw-text-[13px] tw-text-[#76808c]">
                Chargement des valeurs par défaut…
              </p>
            </Panel>
          ) : (
            <>
              {step === STEP.INFORMATION && (
                <InformationStep
                  information={information}
                  setInformation={setInformation}
                  logoFile={logoFile}
                  setLogoFile={setLogoFile}
                />
              )}
              {step === STEP.OPTIONS && (
                <OptionsStep
                  featuresV2={featuresV2}
                  setFeaturesV2={setFeaturesV2}
                  defaultFeaturesV2={defaults?.features_v2}
                  baselineFeaturesV2={baselineFeaturesV2}
                  clientsOptions={clientsOptions}
                  onCopyFromClient={handleCopyFromClient}
                  isCopying={isFetching}
                />
              )}
              {step === STEP.PRICING && (
                <PricingStep pricing={pricing} setPricing={setPricing} />
              )}
              {step === STEP.ADMIN && (
                <AdminUserStep
                  isStaged
                  adminUsers={adminUsers}
                  isEmailSendingEnabled={information.is_email_sending_enabled}
                  onAdd={(adminUser) => {
                    setAdminUsers((previous) => [...previous, adminUser]);
                    return true;
                  }}
                  onRemove={(adminUser) =>
                    setAdminUsers((previous) =>
                      previous.filter(
                        (candidate) => candidate.email !== adminUser.email
                      )
                    )
                  }
                />
              )}
              {step === STEP.SUMMARY && (
                <ClientCreationSummary
                  information={information}
                  featuresV2={featuresV2}
                  pricing={pricing}
                  logoFile={logoFile}
                  adminUsers={adminUsers}
                  steps={STEP}
                  onEdit={setStep}
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="ic-page__footer">
        <MainButton variant="secondary" text="Annuler" onClick={exit} />
        <div className="ic-page__footer-spacer" />
        {step === STEP.SUMMARY && (
          <span className="ic-page__footer-hint">
            Rien n’est enregistré avant la création.
          </span>
        )}
        {step > STEP.INFORMATION && (
          <MainButton
            variant="secondary"
            text="Précédent"
            onClick={() => setStep((previous) => previous - 1)}
            disabled={isCreating}
          />
        )}
        {step < STEP.SUMMARY ? (
          <MainButton
            text={`Suivant : ${STEPS[step + 1].label}`}
            onClick={() => setStep((previous) => previous + 1)}
            disabled={isNextDisabled}
          />
        ) : (
          <MainButton
            text="Créer le client"
            onClick={createClient}
            disabled={!isInformationValid || !isPricingStepValid}
            isLoading={isCreating}
          />
        )}
      </div>

      <GenericModal
        isOpen={isLeaveConfirmOpen}
        setIsOpen={setIsLeaveConfirmOpen}
        title="Abandonner la création"
        size={MODAL_SIZE_STYLE[MODAL_SIZE.MD]}
        secondaryActionText="Continuer la saisie"
        secondaryAction={() => setIsLeaveConfirmOpen(false)}
        mainActionText="Abandonner"
        mainActionVariant="danger"
        mainAction={onExit}
      >
        <p className="tw-m-0 tw-text-sm">
          Le client n’est pas encore créé : la saisie sera perdue.
        </p>
      </GenericModal>
    </div>
  );
}
